/* The dashboard sends chat turns through the shared Runs API (`interface: platform`).
   Workspace reads retain the full run response.

   Watching a run is not here: the stream is `stream.ts`, and the hook that
   folds its events into a draft is `features/chat/hooks/useRunStream.ts`.
   Every mutation invalidates the threads keys, because a run is what writes a
   thread's messages, its runs and its place in the list. */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { clientInfo } from "@/shared/lib/time";

import { agentKeys } from "./agents";
import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";
import { ApiError } from "./errors";
import { threadKeys } from "./threads";

import type { ApiRun, RunStart, RunStarted, RunStopped } from "@/shared/types";

/** `NotFound` (backend/errors): the agent, the agent a message is about, or the conversation. */
const NOT_FOUND = 1010005;

/* ------------------------------ endpoints -------------------------------- */

/** Where the dashboard's chat says it calls from: a teammate, who reads every event. */
const PLATFORM = "platform";
const ASKED = { params: { interface: PLATFORM } };

/** 202: recorded and queued. Without `thread_id` it opens a new thread. On a thread
    whose run waits on this person it is their answer, and that run goes on
    (`how: "continued"`); a stopped or failed run is never carried on — the text is
    a new run that reads the transcript. */
export async function startRun({ agent_id: agentId, ...message }: RunStart): Promise<RunStarted> {
  const body = { agent_id: agentId, ...message, interface: PLATFORM, client: clientInfo() };
  return unwrap(await client.post<Envelope<RunStarted>>("/runs", body));
}

export async function getRun(runId: string): Promise<ApiRun> {
  return unwrap(await client.get<Envelope<ApiRun>>(`/runs/${runId}`));
}

/** Sets a flag and returns; the worker stops at the next safe point. A queued or
    pending run ends `halted` at once. */
export async function stopRun(runId: string): Promise<RunStopped> {
  return unwrap(await client.post<Envelope<RunStopped>>(`/runs/${runId}/stop`, {}, ASKED));
}

/** Refused because the answer did things that regenerating does again: `details` names
    the tools, `[{tool}]`, and `repeatActions` goes ahead. */
export const REGENERATE_REPEATS_ACTIONS = 3050008;

/** 202: the conversation's latest answer written again — the run that wrote it leaves the
    transcript and a new run answers the same message (follow it as a started one). */
export async function regenerateRun(runId: string, repeatActions = false): Promise<RunStarted> {
  return unwrap(
    await client.post<Envelope<RunStarted>>(`/runs/${runId}/regenerate`, { repeat_actions: repeatActions }, ASKED),
  );
}

/** The stream's path, for `readStream`. */
export const runStreamPath = (runId: string) => `/runs/${runId}/stream?interface=${PLATFORM}`;

/* -------------------------------- hooks ---------------------------------- */

function useThreadsMutation<V, T>(mutationFn: (vars: V) => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn, onSuccess: () => qc.invalidateQueries({ queryKey: threadKeys.all }) });
}

/** A refusal for an agent that is gone — the chat's, or the one Builder's picker names — means
    another tab or teammate deleted it: the agents are read again, so it leaves the screen. */
export const useStartRun = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: startRun,
    onSuccess: () => qc.invalidateQueries({ queryKey: threadKeys.all }),
    onError: (error) => {
      if (error instanceof ApiError && error.code === NOT_FOUND) qc.invalidateQueries({ queryKey: agentKeys.all });
    },
  });
};
export const useStopRun = () => useThreadsMutation(stopRun);
/** Silent, and nothing re-read: the chat says what a refusal means (the tools to ask about
    first), and holds the transcript steady until the new run writes it again. */
export const useRegenerateRun = () =>
  useMutation({
    mutationFn: ({ runId, repeatActions }: { runId: string; repeatActions: boolean }) => regenerateRun(runId, repeatActions),
    meta: { silent: true },
  });
